import { useLocale, useStartOfWeek } from '@e2m-customer-portal/hooks';
import { Checkbox, CheckboxButton } from '@e2m-customer-portal/ui';
import { FormControlLabel, Typography } from '@mui/material';
import clsx from 'clsx';
import { setDay } from 'date-fns';
import { range } from 'ramda';
import { memo, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { DayOfWeek } from 'weekstart';

export type WeekdaySelection = Record<DayOfWeek, boolean>;

export const makeWeekdaySelection = (selected: boolean, disabled?: WeekdaySelection | boolean): WeekdaySelection => {
  const dayIsEnabled = (day: DayOfWeek): boolean =>
    (typeof disabled !== 'object' && !disabled) || (typeof disabled === 'object' && !disabled[day]);

  return {
    0: selected && dayIsEnabled(0),
    1: selected && dayIsEnabled(1),
    2: selected && dayIsEnabled(2),
    3: selected && dayIsEnabled(3),
    4: selected && dayIsEnabled(4),
    5: selected && dayIsEnabled(5),
    6: selected && dayIsEnabled(6),
  };
};

export interface WeekdaySelectorProps {
  heading?: string;
  selected?: WeekdaySelection;
  onChange?: (selected: WeekdaySelection) => void;
  disabled?: WeekdaySelection | boolean;
  error?: boolean;
  helperText?: string;
  className?: string;

  /**
   * Treats all input and output values as Python would,
   * meaning 0 is Monday and 6 is Sunday.
   */
  pythonMode?: boolean;
  oneDayMode?: boolean;
}

export function WeekdaySelectorBase({
  heading,
  selected,
  onChange,
  disabled,
  error,
  helperText,
  className,
  pythonMode,
  oneDayMode,
}: Readonly<WeekdaySelectorProps>): ReactElement<WeekdaySelectorProps> {
  const pythonShiftedDay = (day: number | string, reverse?: boolean) =>
    (pythonMode ? (+day + (reverse ? 6 : 1)) % 7 : day) as DayOfWeek;
  const [t] = useTranslation();
  const { code: locale } = useLocale();
  const weekStartsOn = useStartOfWeek();
  const selection = Object.entries(selected ?? makeWeekdaySelection(false, disabled)).reduce(
    (selection, [day, selected]) => ({
      ...selection,
      [pythonShiftedDay(day)]: selected,
    }),
    {} as WeekdaySelection,
  );

  const noneSelected = Object.entries(makeWeekdaySelection(false, disabled)).reduce(
    (selection, [day, selected]) => ({
      ...selection,
      [pythonShiftedDay(day)]: selected,
    }),
    {} as WeekdaySelection,
  );

  const unavailable = Object.entries(typeof disabled === 'object' ? disabled : makeWeekdaySelection(!!disabled)).reduce(
    (unavailable, [day, disabled]) => ({
      ...unavailable,
      [pythonShiftedDay(day)]: disabled,
    }),
    {} as WeekdaySelection,
  );

  const allUnavailable = range(0, 7).every((day) => unavailable[day as DayOfWeek]);

  const allSelected =
    range(0, 7).every((day) => selection[day as DayOfWeek] || unavailable[day as DayOfWeek]) && !allUnavailable;

  const someSelected = range(0, 7).some((day) => selection[day as DayOfWeek]);

  const handleAllChange = onChange && ((): void => onChange(makeWeekdaySelection(!allSelected, disabled)));
  const handleChange =
    (day: DayOfWeek): ((checked: boolean) => void) | undefined =>
    (checked) =>
      onChange?.(
        oneDayMode
          ? Object.entries({ ...noneSelected, [day]: checked }).reduce(
              (selection, [day, selected]) => ({
                ...selection,
                [pythonShiftedDay(day, true)]: selected,
              }),
              {} as WeekdaySelection,
            )
          : Object.entries({ ...selection, [day]: checked }).reduce(
              (selection, [day, selected]) => ({
                ...selection,
                [pythonShiftedDay(day, true)]: selected,
              }),
              {} as WeekdaySelection,
            ),
      );

  return (
    <div className={`flex flex-wrap box-border ${className}`}>
      {heading && (
        <div className={clsx('flex-grow-0 m-0 box-border max-w-full', { ['text-red-200']: error })}>
          <Typography variant="body1">{heading}</Typography>
        </div>
      )}

      {!oneDayMode && (
        <div className="flex-grow-0 m-0 box-border max-w-full basis-full">
          <FormControlLabel
            className="ml-0"
            classes={{ label: 'text-semibold pb-1' }}
            control={
              <Checkbox
                className="m-0"
                checked={allSelected}
                indeterminate={someSelected && !allSelected}
                onChange={handleAllChange}
                disabled={typeof disabled === 'boolean' ? disabled : undefined}
              />
            }
            label={t('WEEKDAY_SELECTOR_SELECT_ALL')}
          />
        </div>
      )}
      <div className="flex w-full gap-1">
        {range(0, 7).map((idx) => {
          const day = ((weekStartsOn + idx) % 7) as DayOfWeek;
          return (
            <div key={idx} className="flex flex-row flex-grow-0 m-0 box-border w-full px-0 pt-1 pb-1">
              <CheckboxButton
                className="rounded-none max-w-full w-full"
                checked={selection[day]}
                disabled={unavailable[day]}
                onChange={handleChange(day)}
              >
                {setDay(new Date(), day, { weekStartsOn }).toLocaleDateString(locale, { weekday: 'short' })}
              </CheckboxButton>
            </div>
          );
        })}
      </div>
      {helperText && (
        <div className={clsx('flex-grow-0 m-0 box-border max-w-full basis-full', { ['text-red-200']: error })}>
          <Typography variant="body1">{helperText}</Typography>
        </div>
      )}
    </div>
  );
}

export const WeekdaySelector = memo(WeekdaySelectorBase);
